import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MapPin } from 'lucide-react';
import { usePlayerData } from './PlayerDataContext';
import { getPlannerMe } from '../../api';

// The player's own schedule (Club Planner, Phase 4). Everything comes from GET /api/planner/me,
// which only ever returns what concerns this player from published weeks: their group's parts of
// a rotation, their own meeting slot, their own gym content. Today first (next item highlighted),
// then the coming days.

const DAYS_AHEAD = 13;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const pad = n => String(n).padStart(2, '0');
const localDate = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (s, n) => { const [y, m, d] = s.split('-').map(Number); return localDate(new Date(y, m - 1, d + n)); };
const fmtDay = s => { const [y, m, d] = s.split('-').map(Number); return `${DOW[new Date(y, m - 1, d).getDay()]} ${d} ${MON[m - 1]}`; };
const isoWeek = s => {
  const [y, m, d] = s.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  return Math.ceil(((t - Date.UTC(t.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
};
const nowHHMM = () => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

function changedText(c) {
  if (!c) return null;
  if (c.kind === 'new') return 'New';
  return c.was_start ? `Moved · was ${c.was_start}` : 'Moved';
}

const VARIANT_TEXT = { played: 'If you played', not_played: "If you didn't play" };

function ItemCard({ item, next, onGym }) {
  const dark = next;
  const sub = [item.end && item.start ? `until ${item.end}` : null, item.location].filter(Boolean);
  return (
    <div style={{
      display: 'flex', gap: 12, borderRadius: 12, padding: dark ? '10px 12px' : '9px 12px',
      background: dark ? '#15171a' : 'var(--bg-card)', color: dark ? '#fff' : 'var(--text-primary)',
      border: dark ? 'none' : '1px solid var(--border)',
    }}>
      <span style={{ fontFamily: MONO, fontSize: 12, width: 44, flexShrink: 0, opacity: dark ? 0.85 : 1, color: dark ? '#fff' : 'var(--text-secondary)', paddingTop: dark ? 14 : 1 }}>
        {item.start || '—'}
      </span>
      <span aria-hidden="true" style={{ width: 4, borderRadius: 2, background: item.color, flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        {dark && <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em', opacity: 0.8 }}>NEXT</span>}
        <span style={{ fontWeight: 600, fontSize: 15 }}>{item.title}</span>
        {(sub.length > 0 || !item.start) && (
          <span style={{ fontSize: 12, opacity: dark ? 0.85 : 1, color: dark ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            {item.location && <MapPin size={11} />}
            {!item.start ? 'Time to be confirmed' : sub.join(' · ')}
          </span>
        )}
        {item.note && <span style={{ fontSize: 12, color: dark ? '#fff' : 'var(--text-secondary)', opacity: dark ? 0.85 : 1 }}>{item.note}</span>}
        {item.topics.length > 0 && (
          <ul style={{ margin: '2px 0 0', paddingLeft: 16, fontSize: 12, color: dark ? '#fff' : 'var(--text-secondary)' }}>
            {item.topics.map(t => <li key={t}>{t}</li>)}
          </ul>
        )}
        {item.specific_points && (
          <span style={{ fontSize: 12, marginTop: 2, padding: '4px 8px', borderRadius: 6, background: dark ? 'rgba(255,255,255,0.12)' : 'var(--bg-subtle)', color: dark ? '#fff' : 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
            {item.specific_points}
          </span>
        )}
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          {item.optional && <Badge text="Optional" />}
          {item.variant && <Badge text={VARIANT_TEXT[item.variant]} />}
          {item.changed && <Badge text={changedText(item.changed)} warn />}
        </span>
        {item.gym_link && (
          <button type="button" onClick={onGym} style={{ alignSelf: 'flex-start', marginTop: 4, background: 'none', border: 'none', padding: 0, color: dark ? '#93c5fd' : '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            See your gym session ›
          </button>
        )}
      </div>
    </div>
  );
}

function Badge({ text, warn }) {
  if (!text) return null;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.03em', padding: '2px 6px', borderRadius: 4, background: warn ? '#f59e0b' : 'rgba(127,127,127,0.18)', color: warn ? '#15171a' : 'inherit' }}>
      {text}
    </span>
  );
}

export default function PlayerSchedule() {
  const navigate = useNavigate();
  const { sessionToken, playerRecord } = usePlayerData();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const today = localDate(new Date());

  useEffect(() => {
    if (!sessionToken) { setError('session'); return; }
    getPlannerMe(sessionToken, today, addDays(today, DAYS_AHEAD))
      .then(setData)
      .catch(e => setError(e.status === 401 ? 'session' : e.message));
  }, [sessionToken, today]);

  const todayDay = data?.days.find(d => d.date === today);
  const now = nowHHMM();
  const nextItem = todayDay?.items.find(i => i.start && (i.end || i.start) > now);
  const later = (data?.days || []).filter(d => d.date > today);
  const goGym = () => navigate('/player');

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-app)', paddingBottom: 28 }}>
      <div style={{ background: '#15171a', color: '#fff', padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="button" onClick={() => navigate('/player/me')}
          style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 2, background: 'none', border: 'none', color: '#fff', opacity: 0.8, padding: 0, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', minHeight: 32 }}>
          <ChevronLeft size={16} /> Me
        </button>
        <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em', opacity: 0.8 }}>MY SCHEDULE · WEEK {isoWeek(today)}</span>
        <span style={{ fontWeight: 800, fontSize: 26 }}>{fmtDay(today)}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {todayDay?.label.text && <span style={{ fontFamily: MONO, fontSize: 11, background: '#fff', color: '#15171a', padding: '2px 7px', borderRadius: 4 }}>{todayDay.label.text}</span>}
          {todayDay?.fixtures.map(f => (
            <span key={f.opponent} style={{ fontSize: 13, opacity: 0.9 }}>{f.opponent} ({f.home_away}){f.kickoff_time ? ` · ${f.kickoff_time}` : ''}</span>
          ))}
          {!todayDay?.fixtures.length && playerRecord?.squad_number && <span style={{ fontSize: 13, opacity: 0.9 }}>#{playerRecord.squad_number}</span>}
        </div>
      </div>

      <div style={{ padding: '12px 14px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {error === 'session' && <Empty>Your session has expired. Sign out and sign in again to see your schedule.</Empty>}
        {error && error !== 'session' && <Empty>Could not load your schedule.</Empty>}
        {!error && !data && <Empty>Loading…</Empty>}
        {data && !todayDay?.published && <Empty>Today's schedule hasn't been published yet.</Empty>}
        {data && todayDay?.published && !todayDay.items.length && <Empty>{todayDay.type === 'off' ? 'Day off.' : 'Nothing planned for you today.'}</Empty>}
        {todayDay?.items.map(item => <ItemCard key={item.id} item={item} next={item === nextItem} onGym={goGym} />)}
      </div>

      {later.length > 0 && (
        <div style={{ padding: '18px 14px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {later.map(d => (
            <section key={d.date} aria-label={fmtDay(d.date)}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtDay(d.date)}</h3>
                {d.label.text && <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--text-secondary)' }}>{d.label.text}</span>}
                {d.fixtures.map(f => <span key={f.opponent} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.opponent} ({f.home_away}){f.kickoff_time ? ` · ${f.kickoff_time}` : ''}</span>)}
              </div>
              {!d.published && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{d.type === 'off' ? 'Day off' : 'Not published yet'}</div>}
              {d.published && !d.items.length && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{d.type === 'off' ? 'Day off' : 'Nothing planned for you'}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {d.items.map(item => <ItemCard key={item.id} item={item} onGym={goGym} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ padding: '14px 12px', borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 14, color: 'var(--text-muted)' }}>{children}</div>;
}
